import React from 'react';
import AriaModal from 'react-aria-modal';
import { CloseIcon } from './../../constants/icon';
import { COMMON } from '../../constants';
import { Form } from 'reactstrap';
import './modal.css';

/**
 * Get the root node
 */
export const getApplicationNode = () => {
    return document.getElementById('root');
};

/**
 * Update when modal is shown
 */
export const modalEntered = () => {
    getApplicationNode() ? (getApplicationNode().tabIndex = '-1') : null;
};

/**
 * Adds a React component to show a popup modal with additional information/actions.
 *
 *  - Roles
 *     - Show a popup modal above current screen.
 *     - Disable actions beneath the popup modal.
 *     - Show additional information on the modal with a close icon.
 *
 * @param modalActive if true, a popup modal is seen on the screen.
 * @param confirmation if true, the popup modal shows two buttons for a positive and negative action.
 * @param title Title to be shown at the top of modal.
 * @param btnTtl Title to be shown on the button with positive action when confirmation is set to true.
 * @param negative if true, the popup modal has two buttons for a positive and negative action. It styles negative action in red color.
 * @param alertModalError if true, changes default button class to error-btn.
 */

const Modal = (props) => {
    /**
     * Update when modal is closed
     */
    const modalExited = () => {
        getApplicationNode()?.removeAttribute?.('tabindex');
        props.deactivateModal();
    };

    return (
        <React.Fragment>
            {props.modalActive && (
                <AriaModal
                    titleText={props.title || 'Dialog'}
                    onEnter={modalEntered}
                    aria-labelledby={props.title ? 'modal-heading' : undefined}
                    onExit={modalExited}
                    underlayClickExits={false}
                    verticallyCenter={true}
                    alert={props.alert}
                    dialogClass={props.dialogClass}
                    underlayClass={'modal-window-root'}
                    getApplicationNode={getApplicationNode}
                >
                    <div
                        id="modal"
                        className="modal modal-content"
                        data-testid="modal-wrapper"
                    >
                        <div
                            className={`modal-body modal-layout modal-body ${props.modalLayoutClassName ?? ''}`}
                        >
                            {props.title && (
                                <div className="modal-header">
                                    <h2
                                        id="modal-heading"
                                        className="modal-title center-align"
                                        data-testid="modal-heading"
                                    >
                                        {props.title}
                                    </h2>
                                </div>
                            )}
                            {props.confirmation ? (
                                <Form
                                    className="form"
                                    data-testid="confirmation-form"
                                >
                                    {props.expandedContent}
                                    <div className="_footer flex flex-center">
                                        <button
                                            id="back_alert_btn"
                                            data-testid="back-alert-btn"
                                            type="button"
                                            style={{
                                                minWidth: '120px',
                                                margin: '0px 10px'
                                            }}
                                            className="back"
                                            onClick={modalExited}
                                        >
                                            {COMMON.cancel}
                                        </button>

                                        {props.additionalButtons &&
                                            props.additionalButtons.length >
                                                0 &&
                                            props.additionalButtons.map(
                                                (button, index) => (
                                                    <button
                                                        key={index}
                                                        type="button"
                                                        style={{
                                                            minWidth: '120px',
                                                            margin: '0px 10px'
                                                        }}
                                                        className={
                                                            button.className ||
                                                            'next'
                                                        }
                                                        disabled={
                                                            button.disabled ||
                                                            false
                                                        }
                                                        onClick={button.action}
                                                        data-testid={`modal-additional-btn-${index}`}
                                                    >
                                                        {button.title}
                                                    </button>
                                                )
                                            )}
                                        {props.btnTtl && (
                                            <button
                                                type="button"
                                                style={{
                                                    minWidth: '120px',
                                                    margin: '0px 10px'
                                                }}
                                                className={
                                                    props.negative
                                                        ? 'negative'
                                                        : 'next'
                                                }
                                                disabled={
                                                    props.disabled
                                                        ? props.disabled
                                                        : false
                                                }
                                                onClick={props.onYesClick}
                                                data-testid="modal-action-btn"
                                            >
                                                {props.btnTtl}
                                            </button>
                                        )}
                                    </div>
                                </Form>
                            ) : (
                                props.expandedContent
                            )}
                            {props.modal_generic_btn && (
                                <div className="_footer flex flex-center">
                                    <button
                                        type="button"
                                        style={{
                                            minWidth: '120px',
                                            margin: '0px 10px'
                                        }}
                                        data-testid="modal-confirmation-btn"
                                        className={
                                            props.alertModalError
                                                ? 'next error-btn'
                                                : 'next'
                                        }
                                        onClick={props.onYesClick}
                                    >
                                        {props.btnTtl}
                                    </button>
                                </div>
                            )}
                            {!props.hideCloseIcon && (
                                <div
                                    className="close-button-wrapper order-close-wrapper"
                                    data-testid="close-button-wrapper"
                                >
                                    <button
                                        className="close-button"
                                        type="button"
                                        onClick={modalExited}
                                        aria-label="Close"
                                        data-testid="close-button"
                                    >
                                        <img
                                            src={CloseIcon}
                                            alt=""
                                            aria-label={COMMON.close}
                                            width="14"
                                            height="14"
                                        />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </AriaModal>
            )}
        </React.Fragment>
    );
};

export default Modal;
